/**
 * fetch wrapper with AbortController timeout and optional external abort signal.
 */

import { TIMEOUT_MS } from './skinResilience';

const DEFAULT_TIMEOUT_MS = TIMEOUT_MS.DEFAULT;

/**
 * @param {RequestInfo | URL} input
 * @param {RequestInit & { timeoutMs?: number }} [init]
 * @returns {Promise<Response>}
 */
export async function fetchWithTimeout(input, init = {}) {
  const { timeoutMs, signal: externalSignal, ...rest } = init || {};
  const ms = Number.isFinite(timeoutMs) ? timeoutMs : DEFAULT_TIMEOUT_MS;

  if (typeof AbortController === 'undefined') {
    return fetch(input, { ...rest, signal: externalSignal });
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    try {
      controller.abort(Object.assign(new Error('timeout'), { reason: 'timeout' }));
    } catch (_) {
      controller.abort();
    }
  }, Math.max(0, ms));

  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else {
      externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    return await fetch(input, { ...rest, signal: controller.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      if (externalSignal?.aborted) {
        throw Object.assign(new Error('Request aborted'), { name: 'AbortError' });
      }
      throw Object.assign(
        new Error(`Request timed out after ${ms}ms`),
        { name: 'AbortError', reason: 'timeout', timeoutMs: ms }
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}
